import React, { useState, useEffect } from 'react';
import { Check, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import searchIcon from '../../assets/icons/search.svg';
import { useAuth } from '@/contexts/AuthContext';

interface Partner {
  id: string;
  full_name: string;
  username: string;
  profile_picture_url?: string;
  belt_rank?: string;
}

interface PartnerAutocompleteProps {
  value: Partner | string | null;
  onChange: (partner: Partner | string | null) => void;
  placeholder?: string;
  className?: string;
  containerClassName?: string;
  disabled?: boolean;
}

export function PartnerAutocomplete({ 
  value, 
  onChange, 
  placeholder = "Type a name or a username", 
  className = "", 
  containerClassName = "",
  disabled = false 
}: PartnerAutocompleteProps) {
  const { user } = useAuth();
  const [filteredPartners, setFilteredPartners] = useState<Partner[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [showManualOption, setShowManualOption] = useState(false);

  // Extract display value from the value prop
  const displayValue = typeof value === 'string' ? value : (value && typeof value === 'object' ? value.full_name : '');

  useEffect(() => {
    setInputValue(displayValue);
  }, [displayValue]);

  // Reset input when value becomes null (after partner selection)
  useEffect(() => {
    if (value === null) {
      setInputValue('');
      setOpen(false);
      setShowManualOption(false);
    }
  }, [value]);

  // Scroll to dropdown when it opens
  useEffect(() => {
    if (open && (filteredPartners.length > 0 || showManualOption)) {
      const dropdown = document.querySelector('[data-partner-dropdown]');
      if (dropdown) {
        dropdown.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }
  }, [open, filteredPartners.length, showManualOption]);

  useEffect(() => {
    const fetchPartners = async () => {
      if (!inputValue || inputValue.length < 2 || !user) {
        setFilteredPartners([]);
        setShowManualOption(false);
        return;
      }
      
      setLoading(true);
      try {
        // Get the session token
        const { data: { session } } = await import('@/integrations/supabase/client').then(m => m.supabase.auth.getSession());
        const token = session?.access_token;

        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/search_partners`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            searchTerm: inputValue,
            limit: 10
          })
        });

        if (response.ok) {
          const result = await response.json();
          const partners = result.data || [];
          setFilteredPartners(partners);
          
          // Show manual option if no exact matches found
          const hasExactMatch = partners.some(
            (partner: Partner) => 
              (partner.full_name?.toLowerCase() === inputValue.toLowerCase()) ||
              (partner.username?.toLowerCase() === inputValue.toLowerCase())
          );
          setShowManualOption(!hasExactMatch && inputValue.length > 0);
        } else {
          console.error('Error fetching partners:', response.statusText);
          setFilteredPartners([]);
          setShowManualOption(false);
        }
      } catch (error) {
        console.error('Error fetching partners:', error);
        setFilteredPartners([]);
        setShowManualOption(false);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchPartners, 300);
    return () => clearTimeout(timeoutId);
  }, [inputValue, user]);

  const handleSelect = (partner: Partner | string) => {
    onChange(partner);
    setInputValue(typeof partner === 'string' ? partner : partner.full_name || '');
    setOpen(false);
    setShowManualOption(false);
  };

  const handleManualAdd = () => {
    onChange(inputValue);
    setInputValue(inputValue);
    setOpen(false);
    setShowManualOption(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setOpen(true);
  };

  const handleInputFocus = () => {
    setOpen(true);
  };

  const handleInputBlur = () => {
    setTimeout(() => setOpen(false), 200);
  };

  return (
    <div className={cn("relative", containerClassName)}>
      <div className="relative">
        <img
          src={searchIcon}
          alt="Search"
          className={cn(
            "absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5",
            containerClassName?.includes('bg-white') || className?.includes('bg-white')
              ? "brightness-0 opacity-40"
              : "text-white/60"
          )}
        />
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "w-full bg-white/5 border border-white rounded-xl py-2.5 pl-12 pr-12 text-white text-[12px] placeholder-white/60 focus:outline-none transition-all duration-200 font-sans",
            // Check if we're in a white background context (like a modal)
            containerClassName?.includes('bg-white') || className?.includes('bg-white')
              ? "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500 rounded-md"
              : "bg-white/5 border-white text-white placeholder-white/60 rounded-xl",
            className
          )}
          style={{ 
            zIndex: 1,
            borderWidth: '1px',
            borderStyle: 'solid'
          }}
        />
        {inputValue && (
          <button
            onClick={() => {
              setInputValue('');
              onChange(null);
            }}
            className={cn(
              "absolute right-4 top-1/2 transform -translate-y-1/2 transition-colors duration-200",
              containerClassName?.includes('bg-white') || className?.includes('bg-white')
                ? "text-gray-400 hover:text-gray-600"
                : "text-white/60 hover:text-white"
            )}
            aria-label="Clear search"
            style={{ zIndex: 2 }}
          >
            <X className="w-5 h-5" />
          </button>
        )}
        {loading && !inputValue && (
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none" style={{ zIndex: 2 }}>
            <div className={cn(
              "w-4 h-4 border-2 rounded-full animate-spin",
              containerClassName?.includes('bg-white') || className?.includes('bg-white')
                ? "border-gray-300 border-t-gray-600"
                : "border-white/30 border-t-white"
            )} />
          </div>
        )}
      </div>

      {open && (filteredPartners.length > 0 || showManualOption) && (
        <div className="absolute z-50 w-full mt-1 bg-black rounded-md shadow-lg max-h-60 overflow-y-auto" data-partner-dropdown>
          {loading ? (
            <div className="p-4 text-center text-sm text-white/60">
              Loading partners...
            </div>
          ) : (
            <div className="py-1">
              {filteredPartners.map((partner) => (
                <div
                  key={partner.id}
                  onClick={() => handleSelect(partner)}
                  className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-lg text-white hover:bg-white/10 focus:bg-white/10"
                >
                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    {inputValue === partner.full_name && <Check className="h-4 w-4 text-white" />}
                  </span>
                  <div className="flex flex-col flex-1">
                    <span className="text-sm font-medium text-white">{partner.full_name || 'Unknown'}</span>
                    <span className="text-xs text-white/60">@{partner.username || 'unknown'}</span>
                  </div>
                </div>
              ))}
              
              {showManualOption && (
                <div
                  onClick={handleManualAdd}
                  className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-lg text-white hover:bg-white/10 focus:bg-white/10 border-t border-white/20"
                >
                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    <Plus className="h-4 w-4 text-white" />
                  </span>
                  <div className="flex flex-col flex-1">
                    <span className="text-sm font-medium text-white">
                      Add "{inputValue}" manually
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
} 